import React, {useEffect, useState} from 'react';
import {useSelector, useDispatch} from 'react-redux';
import PropTypes from 'prop-types';

import styled from 'styled-components';

import {getPercent, stringFormat} from '@ey/utils';
import {getTheme} from '@ey/styled-theme';

import {labels} from '../../../../../util/utils';
import Utility from '../../../../../util/utilityfunctions';
import {dashboardTabId, ccpMetricStatus} from '../../../../../util/uiconstants';

import MetricsTile from '../../../../Common/Tile/MetricsTile';
import EmptyMessage from '../../../../Common/Messages/EmptyMessage';
import MetricsTileLabel from '../../../../Common/Tile/MetricsTileLabel';

import {
	updateFilterDashboardTiles,
	clearFilterDashboardTiles
} from '../../../../../actions/Metrics/dashboardFiltersAction';

import normalizeDashboardFilters from '../../../../../util/dashboard/normalizeDashboardFilters';

const CCPMetrics = (props) => {
	const {engagementMetrics} = props;
	const [items, setItems] = useState([]);
	const [hasData, setHasData] = useState(false);
	const numberFormat = useSelector((store) => store.userPreferences.numberFormat);
	const currentTheme = getTheme(Utility.isDarkMode());
	const dispatch = useDispatch();

	useEffect(() => {
		if (engagementMetrics && engagementMetrics.length !== 0) {
			loadMetricsData();
			setHasData(true);
		} else {
			setHasData(false);
		}
	}, [engagementMetrics]);

	const loadMetricsData = () => {
		let total = 0;
		let overdue = 0;
		let received = 0;
		let accepted = 0;
		let returned = 0;
		let averageDaySum = 0;
		let draft = 0;

		engagementMetrics.forEach((element) => {
			total += element.cRTotal;
			if (element.cRCurDue) overdue += element.cRCurDue;
			returned += element.cRRetrn;
			received += element.cRReceived;
			accepted += element.cRAccepted;
			averageDaySum += element.cRAvgDays;
			draft = draft + element.cRNotSent;
		});

		let outstanding = total - draft - received - accepted;

		let data = [
			getReceivedOnTimeMetrics(accepted, received),
			getReturnedMetrics(returned, outstanding),
			getAverageDaysOverdueMetrics(overdue, outstanding, averageDaySum, engagementMetrics.length)
		];

		setItems(data);
	};

	const getReceivedOnTimeMetrics = (accepted, received) => {
		let total = accepted + received;
		let value = `${Utility.getFormattedNumber(numberFormat, total > 0 ? getPercent(received, total) : 0)}%`;
		let content = stringFormat(
			labels.outOfReceivedAndAccepted,
			Utility.getFormattedNumber(numberFormat, received),
			Utility.getFormattedNumber(numberFormat, total)
		);

		return getMetricTileLabel(
			value,
			content,
			labels.receivedByEyOnTime,
			currentTheme.color[`grey500`],
			ccpMetricStatus.acceptedReceived
		);
	};

	const getReturnedMetrics = (returned, outstanding) => {
		let value = `${Utility.getFormattedNumber(numberFormat, outstanding > 0 ? getPercent(returned, outstanding) : 0)}%`;
		let content = stringFormat(
			labels.outOfStandingRequests,
			Utility.getFormattedNumber(numberFormat, returned),
			Utility.getFormattedNumber(numberFormat, outstanding)
		);

		return getMetricTileLabel(
			value,
			content,
			labels.returned,
			currentTheme.color[`grey500`],
			ccpMetricStatus.returnedOutstanding
		);
	};

	const getAverageDaysOverdueMetrics = (overdue, outstanding, averageDaySum, engagementsCount) => {
		/* TODO: Average days overdue metrics value is not using users preference format 
				because @ey/utils formatter currently doesn't work with floating point. */

		let result = 0;
		if (engagementsCount > 0) {
			const average = averageDaySum / engagementsCount;
			const averageFixed = average.toFixed(1);
			result = average > 0 && averageFixed !== '0.0' ? averageFixed : 0;
		}

		let content = stringFormat(
			labels.outOfAverageDaysOverdue,
			Utility.getFormattedNumber(numberFormat, overdue),
			Utility.getFormattedNumber(numberFormat, outstanding)
		);

		result += '%';

		return getMetricTileLabel(
			result,
			content,
			labels.averageDaysOverdue,
			currentTheme.color[`grey500`],
			ccpMetricStatus.overdueOutstandingAverageDaySum
		);
	};

	const getMetricTileLabel = (value, content, label, color, countProp) => {
		const handlerOnGraphicClick = () => {
			let chipLabel;
			const getFilteredEngagemets = () => {
				switch (countProp) {
					case ccpMetricStatus.acceptedReceived:
						chipLabel = labels.received;
						return props.engagementMetrics.filter((item) => item.cRReceived > 0 && item.cRAccepted > 0);
					case ccpMetricStatus.returnedOutstanding:
						chipLabel = labels.returned;
						return props.engagementMetrics.filter((item) => item.cRRetrn > 0);
					case ccpMetricStatus.overdueOutstandingAverageDaySum:
						chipLabel = labels.overdue.charAt(0).toUpperCase() + labels.overdue.slice(1);
						return props.engagementMetrics.filter((item) => item.cRCurDue > 0 && item.cRAvgDays > 0);
				}
			};

			const engagements = getFilteredEngagemets();
			const handlerOnClearFilter = () => {
				dispatch(clearFilterDashboardTiles(dashboardTabId.canvasClientPortal));
			};

			const dashboardFilters = normalizeDashboardFilters(
				[
					{
						categoryLabel: chipLabel,
						onClearChip: handlerOnClearFilter
					}
				],
				engagements,
				handlerOnClearFilter
			);

			dispatch(updateFilterDashboardTiles(dashboardTabId.canvasClientPortal, dashboardFilters));
		};

		let result = {
			label: <MetricsTileLabel title={label} content={content} onGraphicClick={handlerOnGraphicClick} />,
			value: value,
			valueColor: color
		};

		return result;
	};

	return (
		<MetricsTile
			title={labels.metrics}
			items={items}
			showMessage={!hasData}
			noResultFoundMessage={
				<EmptyMessage title={labels.thereIsNoDataToDisplay} subTitle={labels.selectadditionalEntitiesToContinue} />
			}
		/>
	);
};

CCPMetrics.defaultProps = {
	engagementMetrics: []
};

CCPMetrics.propTypes = {
	engagementMetrics: PropTypes.arrayOf(
		PropTypes.shape({
			cRAccepted: PropTypes.number,
			cRDue3: PropTypes.number,
			cRDue5: PropTypes.number,
			cRDue10: PropTypes.number,
			cRDue30: PropTypes.number,
			cRDueTom: PropTypes.number,
			cRNotSent: PropTypes.number,
			cROD: PropTypes.number,
			cRPendResp: PropTypes.number,
			cRReceived: PropTypes.number,
			cRRetrn: PropTypes.number,
			cRTotal: PropTypes.number,
			cTComplSend: PropTypes.number,
			cTNotSent: PropTypes.number,
			cTOD: PropTypes.number,
			cTPartialSend: PropTypes.number,
			cTRetrn: PropTypes.number,
			cTTotal: PropTypes.number,
			cTUnlinkTot: PropTypes.number,
			conTaskClo: PropTypes.number,
			conTaskInProg: PropTypes.number,
			conTaskInRev: PropTypes.number,
			conTaskOpen: PropTypes.number,
			conTaskTotal: PropTypes.number,
			conTested: PropTypes.number,
			conTotal: PropTypes.number,
			defDesign: PropTypes.number,
			defOper: PropTypes.number,
			defOperAndDes: PropTypes.number,
			engDes: PropTypes.string,
			engId: PropTypes.number,
			engKey: PropTypes.number,
			gAType: PropTypes.number,
			hasAPEQRSO: PropTypes.bool,
			hasAPPICSO: PropTypes.bool,
			hasCRAEQRSO: PropTypes.bool,
			hasCRAPICSO: PropTypes.bool,
			isEQRCRASODlyd: PropTypes.bool,
			isEQREng: PropTypes.bool,
			isEQRInv: PropTypes.bool,
			isPartAPSODlyd: PropTypes.bool,
			isPartCRASODlyd: PropTypes.bool,
			isPartInvDlyd: PropTypes.bool
		})
	)
};

export default CCPMetrics;
